// ChatBox.js
import React, {Component} from 'react';
import {ChatRoom, SendMessageRequest} from 'amazon-ivs-chat-messaging';
import {rwbApi} from '../../../../shared/apis/api';
import DefaultProfileImg from '../../../../shared/project-info/images/DefaultProfileImg.png';
import styles from './ChatBox.module.css';
import ChevronDownIcon from '../svgs/ChevronDownIcon';
import PostIcon from '../svgs/PostIcon';

class Message extends Component {
  renderTextWithLinks(text) {
    const urlPattern = /(\bhttps?:\/\/[^\s]+)/g;
    const parts = text.split(urlPattern);

    return parts.map((part, index) => {
      if (part.match(urlPattern)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}>
            {part}
          </a>
        );
      } else {
        return <span key={index}>{part}</span>;
      }
    });
  }

  render() {
    const {text, username, profileImage, isRight} = this.props;
    const imageSource = profileImage ? profileImage : DefaultProfileImg;

    return (
      <div className={`${styles.messageContainer} ${styles.leftJustify}`}>
        <img src={imageSource} alt="Profile" className={styles.profileImage} />
        <div className={styles.textContainer}>
          <div className={styles.username}>{username}</div>
          <div className={styles.messageText}>
            {this.renderTextWithLinks(text)}
          </div>
        </div>
      </div>
    );
  }
}

class ChatBox extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chatState: '🔴',
      isMinimized: true,
      messages: [],
      inputText: '',
    };
    this.chatRoom = null;
  }

  setupStateChangeListeners = () => {
    if (!this.chatRoom) {
      console.error('Chat room is not initialized.');
      return;
    }

    this.onConnecting = this.chatRoom.addListener('connecting', () => {
      this.setState({chatState: 'connecting'});
    });

    this.onConnected = this.chatRoom.addListener('connect', () => {
      this.setState({chatState: '🟢'});
    });

    this.onDisconnected = this.chatRoom.addListener('disconnect', () => {
      this.setState({chatState: 'disconnected'});
    });

    this.onMessageReceived = this.chatRoom.addListener('message', (message) => {
      this.handleNewMessage(message);
    });
  };

  initializeLiveChat = async () => {
    try {
      this.chatRoom = new ChatRoom({
        regionOrUrl: 'wss://edge.ivschat.us-east-1.amazonaws.com',
        tokenProvider: async () => {
          var token = await rwbApi.getChatToken('event_1');
          return token;
        },
      });
      this.chatRoom.connect();
      this.setupStateChangeListeners();
    } catch (error) {
      console.error('Failed to initialize chat:', error);
    }
  };

  getChatLogsFromUrl(url) {
    console.log('Fetching chat logs from URL', url);
    this.setState({chatState: ''});
    var idx = 0;
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const lines = text.split('\n');
        const parsedData = lines
          .map((line) => {
            const parts = line.split('\t');
            if (parts.length === 3) {
              return {
                time: parts[0],
                username: parts[1],
                content: parts[2],
              };
            }
            return null;
          })
          .filter(Boolean);

        var messages = [];
        parsedData.forEach(function (message) {
          idx += 1;
          const newMessage = {
            id: idx,
            text: message.content.trim(),
            username: message.username,
            isRight: false,
          };
          messages = [newMessage, ...messages];
        });
        this.setState({messages: messages});
      })
      .catch((error) => {
        console.error('Error fetching the chat logs:', error);
      });
  }

  componentDidMount() {
    const liveChat = false;
    if (this.props.chatLogUrl) {
      this.getChatLogsFromUrl(this.props.chatLogUrl);
    } else if (liveChat) {
      rwbApi
        .getChatLogs('event_1')
        .then((res) => {
          res.forEach((log) => {
            const message = log.payload;
            this.handleNewMessage(message);
          });
          this.initializeLiveChat();
        })
        .catch((err) => {});
    }
  }

  componentWillUnmount() {
    if (this.chatRoom) {
      this.chatRoom.disconnect();
    }
  }

  handleNewMessage = (message) => {
    this.setState((prevState) => {
      const newMessage = {
        id: message.id,
        text: message.content,
        profileImage: message.sender.attributes.profile_photo_url,
        username: message.sender.attributes.first_name,
        isRight: false,
      };

      return {messages: [newMessage, ...prevState.messages]};
    });
  };

  sendMessage = () => {
    const {inputText} = this.state;
    if (inputText.trim()) {
      const request = new SendMessageRequest(inputText);
      this.chatRoom.sendMessage(request);
      this.setState({inputText: ''});
    }
  };

  toggleMinimize = () => {
    this.setState((prevState) => ({isMinimized: !prevState.isMinimized}));
  };

  renderMinimizedView = () => {
    const {messages} = this.state;
    const lastMessage = messages.length > 0 ? messages[0] : null;
    if (!lastMessage) {
      return null;
    }

    return (
      <div className={styles.flexContainer}>
        <div className={styles.chatStateContainer}>
          <div>{this.state.chatState}</div>
        </div>
        <button onClick={this.toggleMinimize} className={styles.toggleButton}>
          <h4>
            {this.state.chatState === '🟢' ? 'Live Chat' : 'Recorded Chat'}
          </h4>
          <ChevronDownIcon tintColor={'var(--grey)'} />
        </button>
      </div>
    );
  };

  renderMessageItem = (item) => (
    <Message
      key={item.id}
      username={item.username}
      text={item.text}
      profileImage={item.profileImage}
      isRight={true}
    />
  );

  render() {
    const {isMinimized, messages, inputText} = this.state;

    return (
      <div className={styles.chatContainer}>
        {!isMinimized ? (
          <div className={styles.flexContainer}>
            <button
              onClick={this.toggleMinimize}
              className={styles.toggleButton}>
              <h4>
                {this.state.chatState === '🟢' ? 'Live Chat' : 'Recorded Chat'}
              </h4>
              <ChevronDownIcon
                tintColor={'var(--grey)'}
                className={styles.flipChevron}
              />
            </button>
            <div className={styles.messagesList}>
              {messages.map(this.renderMessageItem)}
            </div>
            {this.state.chatState === '🟢' && (
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => this.setState({inputText: e.target.value})}
                  placeholder="Type a message..."
                  className={styles.input}
                />
                <button
                  onClick={this.sendMessage}
                  className={styles.sendButton}>
                  <PostIcon
                    width={16}
                    height={16}
                    className={styles.sendButtonIcon}
                  />
                </button>
              </div>
            )}
          </div>
        ) : (
          this.renderMinimizedView()
        )}
      </div>
    );
  }
}

export default ChatBox;
